<!--
  ConversationListItem — polished per DESIGN.md "Brushed Steel + Signal Amber".

  The signature row of the Operator Station. Every row carries:
    [ avatar 36 ] [ name + preview ] [ time + channel chip + status chip ]

  Selection is tonal recession to Steel BG Sunk (not accent tint). Hover is
  6% amber wash. Avatar is neutral (color-blind safe, no rainbow hues).
  Channel + status colors live exclusively inside pill chips paired with
  glyphs (M / dot) per the Glyph Pairing Rule.
-->
<script lang="ts">
  import type { Conversation } from '../lib/api/queries.js';
  import { nowTick } from '../lib/realtime/now-tick.svelte.js';
  import { session } from '../lib/stores/session.svelte.js';

  type Props = {
    conversation: Conversation;
    selected?: boolean;
    focused?: boolean;
    onActivate?: (id: string) => void;
  };

  let { conversation, selected = false, focused = false, onActivate }: Props = $props();

  const name = $derived(displayName(conversation));
  const preview = $derived(conversation.lastMessagePreview?.trim() || '');
  const unread = $derived(conversation.unreadCount ?? 0);
  const unreadLabel = $derived(unread > 99 ? '99+' : String(unread));
  const initials = $derived(extractInitials(name));
  const relative = $derived(formatRelative(conversation.lastMessageAt));
  const channel = $derived(channelGlyph(conversation.channelType));

  // G1.6 — first N applied tags as colored chips; rest collapse into "+N"
  // overflow pill. Match admin's TAG_CHIP_VISIBLE_MAX = 2 so widget +
  // admin show the same density.
  const TAG_CHIP_VISIBLE_MAX = 2;
  const allTags = $derived(conversation.appliedTags ?? []);
  const visibleTags = $derived(allTags.slice(0, TAG_CHIP_VISIBLE_MAX));
  const overflowTagCount = $derived(Math.max(0, allTags.length - TAG_CHIP_VISIBLE_MAX));

  // G3.3 — first-response SLA chip. Renders only when the server has set
  // awaitingResponseSince (i.e. the operator owes a reply); breached
  // state pulls the danger palette.
  // Server-computed: the operator should never have to open a thread to find
  // out it cannot be replied to.
  const sendFailed = $derived(conversation.sendFailed === true);
  const windowClosed = $derived(conversation.windowClosed === true);

  const awaitingSince = $derived(conversation.awaitingResponseSince ?? null);
  const slaBreached = $derived(conversation.slaBreached === true);
  const slaLabel = $derived(awaitingSince ? formatWaiting(awaitingSince) : '');

  // G4.3c — row-level assignee chip (avatar-only). Renders ONLY when
  // assigned to keep the chip strip compact — operators using the
  // "Unassigned" filter already know their view's unowned rows. Same
  // initials algorithm as AssigneePicker + panel chip so the avatar
  // text is identical across all three surfaces.
  const rowAssignee = $derived(conversation.assignee ?? null);
  const rowAssigneeInitials = $derived(extractAssigneeInitials(rowAssignee?.displayName ?? null));

  // M2.11 — "mine" highlight for the assignee chip. currentUserId comes
  // from the SSO session (set by bootstrap.ts); before SSO it's null and no
  // row matches, which is the right default for the pre-auth gate.
  //
  // The countdown that used to live beside it is gone with the claim TTL
  // (2026-09-07): the chip now says who holds the row, and nothing else,
  // because nothing about it changes while the operator watches.
  const currentUserId = $derived(session.state.sso?.user?.id ?? null);
  const assigneeIsMine = $derived(
    rowAssignee !== null && currentUserId !== null && rowAssignee.userId === currentUserId,
  );

  function extractAssigneeInitials(displayName: string | null): string {
    const trimmed = (displayName ?? '').trim();
    if (trimmed.length === 0) return '?';
    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
    return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  }

  function formatWaiting(iso: string): string {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    // G3.4-B — read nowTick.value inside the derived so the label
    // re-renders every 60s without the component owning its own timer.
    const elapsed = Math.max(0, nowTick.value - then);
    const min = 60_000;
    const hr = 60 * min;
    const day = 24 * hr;
    if (elapsed < min) return '<1m';
    if (elapsed < hr) return `${Math.floor(elapsed / min)}m`;
    if (elapsed < day) return `${Math.floor(elapsed / hr)}h`;
    return `${Math.floor(elapsed / day)}d`;
  }

  function displayName(c: Conversation): string {
    const fromBackend = c.customerName?.trim();
    if (fromBackend) return fromBackend;
    // Fallback: never "Unmatched contact" (reads as system gap). Show the
    // last 9 of the external sender or thread ref so each row stays
    // identifiable even before the host CRM match lands.
    const ref = c.customerId ?? c.externalThreadRef ?? c.id;
    const tail = ref.slice(-9);
    return `Customer ${tail}`;
  }

  function extractInitials(n: string): string {
    if (n.startsWith('Customer ')) return '·';
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '·';
    const first = parts[0]?.[0] ?? '';
    const second = parts[1]?.[0] ?? '';
    return (first + second).toUpperCase() || '·';
  }

  function formatRelative(iso?: string | null): string {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const diffMs = Date.now() - then;
    const min = Math.floor(diffMs / 60_000);
    if (min < 1) return 'now';
    if (min < 60) return `${min}m`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h`;
    const day = Math.floor(hr / 24);
    if (day < 7) {
      return new Date(then).toLocaleDateString(undefined, { weekday: 'short' });
    }
    return new Date(then).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  function channelGlyph(
    channelType?: string,
  ): { letter: string; hue: string; label: string } | null {
    // META today is Facebook Messenger only; Instagram and WhatsApp become
    // distinguishable once the backend separates them.
    //
    // `label` is the accessible name. The letter alone told a screen-reader
    // user "Channel" and nothing else, so which channel a row arrived on —
    // part of triage, since operators work several pages at once — was
    // sighted-only information.
    switch ((channelType ?? '').toUpperCase()) {
      case 'META':
        return { letter: 'M', hue: 'var(--widget-channel-fb)', label: 'Messenger' };
      case 'WIDGET':
        return { letter: 'W', hue: 'var(--widget-fg-muted)', label: 'Web widget' };
      case 'INTERNAL':
        return { letter: 'I', hue: 'var(--widget-fg-muted)', label: 'Internal' };
      default:
        return null;
    }
  }

  function handleClick() {
    onActivate?.(conversation.id);
  }

  function handleKey(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onActivate?.(conversation.id);
    }
  }
</script>

<div
  class="row"
  class:selected
  class:has-unread={unread > 0}
  class:window-closed={windowClosed}
  role="option"
  aria-selected={selected}
  tabindex={focused ? 0 : -1}
  onclick={handleClick}
  onkeydown={handleKey}
>
  <div class="avatar" aria-hidden="true">
    {#if conversation.customerProfilePicUrl}
      <img src={conversation.customerProfilePicUrl} alt="" loading="lazy" referrerpolicy="no-referrer" />
    {:else}
      <span class="initials">{initials}</span>
    {/if}
  </div>

  <div class="meta">
    <div class="name" title={name}>{name}</div>
    {#if preview}
      <div class="preview" title={preview}>{preview}</div>
    {:else}
      <div class="preview empty">No messages yet</div>
    {/if}
    <!--
      Source and tags share ONE line so the row height never depends on
      optional content. Previously tags and the page name were separate
      stacked lines, so a tagged row stood ~17px taller than an untagged one
      and the eye could not predict where the next name began — the worst
      thing you can do to a list someone scans for eight hours. Overflow
      clips; tags already collapse to "+N".
    -->
    {#if channel || conversation.pageName || allTags.length > 0}
      <div class="subline">
        {#if channel || conversation.pageName}
          <span
            class="source"
            title={conversation.pageName
              ? `${channel?.label ?? 'Channel'} · ${conversation.pageName}`
              : (channel?.label ?? 'Channel')}
          >
            {#if channel}
              <span class="chip-glyph" style="color: {channel.hue}" aria-hidden="true"
                >{channel.letter}</span
              >
              <span class="sr-only">{channel.label ?? 'Channel'}</span>
            {/if}
            {#if conversation.pageName}
              <span class="page">{conversation.pageName}</span>
            {/if}
          </span>
        {/if}
        {#if allTags.length > 0}
          <span class="tags" aria-label="Tags">
            {#each visibleTags as t (t.id)}
              <span
                class="tag-chip"
                title={t.name}
                style="background-color: color-mix(in oklch, {t.color} 22%, transparent); color: color-mix(in oklch, {t.color} 70%, black); border-color: color-mix(in oklch, {t.color} 40%, transparent);"
              >{t.name}</span>
            {/each}
            {#if overflowTagCount > 0}
              <span class="tag-chip overflow">+{overflowTagCount}</span>
            {/if}
          </span>
        {/if}
      </div>
    {/if}
  </div>

  <div class="right">
    {#if relative}
      <div class="time" aria-label="Last activity">{relative}</div>
    {/if}
    <div class="chips">
      {#if rowAssignee}
        <span
          class="chip-assignee"
          class:mine={assigneeIsMine}
          title={assigneeIsMine
            ? 'Assigned to you'
            : `Assigned to ${rowAssignee.displayName ?? 'Operator'}`}
          aria-label={assigneeIsMine
            ? 'Assigned to you'
            : `Assigned to ${rowAssignee.displayName ?? 'Operator'}`}
        >
          <span class="chip-initials">{rowAssigneeInitials}</span>
        </span>
      {/if}
      <!--
        Fixed slots. Each reserves its width whether or not it is occupied, so
        the SLA clock and the unread count each sit on their own vertical line
        down the list. Without this the rightmost position holds a different
        signal on every row and there is nothing to scan.
      -->
      <!--
        "Can I act on this?" — its own slot, because it is a different question
        from "how late is it?" and the two are often true together. A failed
        send outranks a closed window: a stuck outbound is something to fix, a
        closed window is something to skip.
      -->
      <span class="slot slot-blocker">
        {#if sendFailed}
          <span class="chip-blocker failed" title="Last message failed to send" aria-label="Last message failed to send">
            <svg class="blocker-glyph" viewBox="0 0 16 16" aria-hidden="true">
              <path
                fill="currentColor"
                d="M8 1.5 15 14H1L8 1.5Zm0 4.25a.75.75 0 0 0-.75.75v2.75a.75.75 0 0 0 1.5 0V6.5A.75.75 0 0 0 8 5.75Zm0 5.5a.9.9 0 1 0 0 1.8.9.9 0 0 0 0-1.8Z"
              />
            </svg>
          </span>
        {:else if windowClosed}
          <span
            class="chip-blocker closed"
            title="24-hour reply window closed — a normal reply will be rejected"
            aria-label="24-hour reply window closed"
          >
            <svg class="blocker-glyph" viewBox="0 0 16 16" aria-hidden="true">
              <path
                fill="currentColor"
                d="M8 1a3.5 3.5 0 0 0-3.5 3.5V6H4a1.5 1.5 0 0 0-1.5 1.5v5A1.5 1.5 0 0 0 4 14h8a1.5 1.5 0 0 0 1.5-1.5v-5A1.5 1.5 0 0 0 12 6h-.5V4.5A3.5 3.5 0 0 0 8 1Zm2 5H6V4.5a2 2 0 1 1 4 0V6Z"
              />
            </svg>
          </span>
        {/if}
      </span>
      <span class="slot slot-sla">
        {#if awaitingSince}
          <span
            class="chip-sla"
            class:breached={slaBreached}
            title={slaBreached ? 'SLA breached' : 'Awaiting first response'}
            aria-label={slaBreached
              ? `SLA breached, waiting ${slaLabel}`
              : `Waiting ${slaLabel} for first reply`}
          >
            {#if !slaBreached}<span class="chip-dot" aria-hidden="true"></span>{/if}
            {slaLabel}
          </span>
        {/if}
      </span>
      <span class="slot slot-unread">
        {#if unread > 0}
          <span class="chip-unread" aria-label="{unread} unread">{unreadLabel}</span>
        {/if}
      </span>
    </div>
  </div>
</div>

<style>
  .row {
    display: grid;
    grid-template-columns: 36px 1fr auto;
    column-gap: 12px;
    align-items: center;
    padding: 10px 14px;
    /* One height, independent of content. A tagged row used to stand taller
       than an untagged one, so the eye could not predict where the next name
       began — the worst thing you can do to a list scanned for eight hours.
       border-box so this is the TOTAL height: 81px matches what an untagged
       row already measured, making the list uniform without making it taller.
       Fits three lines (20 + 16 + 16) inside 60px of content box, and clears
       the 44px pointer-target minimum twice over. */
    box-sizing: border-box;
    height: 81px;
    overflow: hidden;
    background: transparent;
    color: var(--widget-fg);
    cursor: pointer;
    transition: background-color 120ms cubic-bezier(0.25, 1, 0.5, 1);
    outline: none;
    border-bottom: 1px solid var(--widget-divider);
  }
  .row:hover {
    /* 6% amber wash. Hover earns color; selection earns recession. */
    background: color-mix(in oklch, var(--widget-accent) 6%, transparent);
  }
  .row:focus-visible {
    outline: 2px solid var(--widget-accent);
    outline-offset: -2px;
  }
  .row.selected {
    /* Tonal recession to Steel BG Sunk per DESIGN.md Flat-Surface Rule. */
    background: var(--widget-bg-sunk);
  }
  .row.selected .name {
    color: var(--widget-accent-deep);
  }
  .row:active {
    opacity: 0.85;
  }

  .avatar {
    width: 36px;
    height: 36px;
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: var(--widget-bg-sunk);
    color: var(--widget-fg-muted);
    overflow: hidden;
  }
  .avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .initials {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.02em;
  }

  .meta {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .name {
    font-size: 13.5px;
    font-weight: 600;
    line-height: 1.3;
    color: inherit;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .row.has-unread .name {
    font-weight: 700;
  }
  .preview {
    font-size: 12.5px;
    font-weight: 400;
    line-height: 1.3;
    color: var(--widget-fg-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .page {
    font-size: 11px;
    font-weight: 500;
    line-height: 1.2;
    color: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 50%, transparent);
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }

  /* Source + tags on ONE line, clipped rather than wrapped, so optional
     content can never change the row's height. */
  .subline {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    margin-top: 3px;
    overflow: hidden;
    white-space: nowrap;
  }
  .source {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    flex: 0 1 auto;
  }

  /* G1.6 — tag chip strip. NOWRAP now that it shares the subline; tags
     already collapse to "+N" past TAG_CHIP_VISIBLE_MAX. */
  .tags {
    display: inline-flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 4px;
    min-width: 0;
    overflow: hidden;
  }
  .tag-chip {
    display: inline-flex;
    align-items: center;
    padding: 1px 7px;
    border-radius: 999px;
    border: 1px solid transparent;
    font-size: 10.5px;
    font-weight: 600;
    line-height: 1.4;
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .tag-chip.overflow {
    background: color-mix(in oklch, var(--widget-fg) 8%, transparent);
    color: var(--widget-fg-muted);
    border-color: color-mix(in oklch, var(--widget-fg) 14%, transparent);
  }
  .preview.empty {
    font-style: italic;
    color: var(--widget-fg-dim);
  }

  .right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    min-width: 36px;
  }
  .time {
    font-family: var(--widget-font-mono);
    font-size: 11.5px;
    font-weight: 500;
    color: var(--widget-fg-dim);
    line-height: 1;
    letter-spacing: 0;
    font-variant-numeric: tabular-nums;
  }
  .chips {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  /* Each slot reserves its width even when empty, so a given signal is always
     in the same place down the list. */
  .slot {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    flex: 0 0 auto;
  }
  .slot-blocker {
    min-width: 20px;
  }
  .slot-sla {
    min-width: 34px;
  }

  /* Solid, like a breached SLA — same severity family. It carries a GLYPH
     rather than a duration, so the two never read as the same object when
     they co-occur. */
  .chip-blocker {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 999px;
  }
  .chip-blocker.failed {
    background: var(--widget-danger);
    color: var(--widget-bg-elevated);
  }
  /* A closed window is inert, not urgent — neutral fill so it reads as "skip
     this" rather than competing with the states that need action. */
  .chip-blocker.closed {
    background: var(--widget-fg-muted);
    color: var(--widget-bg-elevated);
  }
  .blocker-glyph {
    width: 11px;
    height: 11px;
  }

  /* A thread that cannot be replied to should recede from a queue being
     scanned, not merely carry a marker — the eye skips it without reading.
     The blocker chip stays at full strength so the reason is still legible. */
  .row.window-closed:not(:hover):not(:focus-visible) .name,
  .row.window-closed:not(:hover):not(:focus-visible) .preview,
  .row.window-closed:not(:hover):not(:focus-visible) .subline,
  .row.window-closed:not(:hover):not(:focus-visible) .time {
    opacity: 0.55;
  }
  .slot-unread {
    min-width: 18px;
  }

  /* Channel glyph. Now sits with the page name in the subline rather than in
     the right-hand chip strip: the channel and the page it arrived on are one
     fact — which of our pages this came from — and splitting them put the
     least actionable signal in the most scannable column. Tertiary palette per
     DESIGN.md still lives only in the glyph, never on a full surface. */
  .chip-glyph {
    display: inline-grid;
    place-items: center;
    width: 16px;
    height: 16px;
    border-radius: 999px;
    background: currentColor;
    color: var(--widget-bg-elevated);
    font-family: var(--widget-font-mono);
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0;
    line-height: 1;
  }

  /* Unread is deliberately QUIET and NEUTRAL.
     It previously used the accent tint — pixel-identical to the SLA chip —
     so an unread count and a first-response clock were indistinguishable in
     the same row. "There are messages" is also the least decision-relevant
     signal here, so accent is now reserved for the clock and unread recedes
     to neutral. The dot went with it: nothing is being paired against hue. */
  .chip-unread {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    padding: 2px 6px;
    background: color-mix(in oklch, var(--widget-fg) 8%, transparent);
    color: var(--widget-fg-muted);
    border-radius: 999px;
    font-family: var(--widget-font-mono);
    font-size: 10.5px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0;
    font-variant-numeric: tabular-nums;
  }
  .row.has-unread .chip-unread {
    color: var(--widget-fg);
  }
  .chip-dot {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: currentColor;
  }

  /* G3.3 — first-response SLA chip.
     Approaching is an OUTLINE, breached is a SOLID fill. Deliberately not
     amber-tint vs danger-tint: two tinted pills that differ only in hue are
     the worst possible pair for deuteranopia, where both collapse to the same
     olive and the operator has to READ the label instead of scanning it.
     Fill-vs-outline is a luminance difference, so it survives every CVD type,
     greyscale, and a bad monitor. */
  .chip-sla {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    background: transparent;
    border: 1px solid var(--widget-accent-deep);
    color: var(--widget-accent-deep);
    border-radius: 999px;
    font-family: var(--widget-font-mono);
    font-size: 10.5px;
    font-weight: 700;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }
  .chip-sla.breached {
    background: var(--widget-danger);
    border-color: var(--widget-danger);
    /* Elevated surface, not raw white: the widget has no on-danger token and
       bg-elevated is the lightest surface in the palette. */
    color: var(--widget-bg-elevated);
  }

  /* G4.3c / M2.11 — assignee chip. An 18px circle carrying initials.
     One variant:
       .mine — outlined-on-accent, distinguishes the operator's own
               rows at a glance.
     The `.claim` / `.expired` pill variants went with the claim TTL. */
  .chip-assignee {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--widget-accent, oklch(75% 0.165 75));
    color: var(--widget-fg, oklch(20% 0.01 250));
    font-family: var(--widget-font);
    font-size: 9.5px;
    font-weight: 700;
    letter-spacing: 0;
    line-height: 1;
  }
  .chip-initials {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: inherit;
    color: inherit;
  }
  .chip-assignee.mine {
    /* Tonal step deeper than the default so "mine" stands out without
       relying on a second hue — preserves DESIGN.md's color-blind-safe
       pairing (initials + position both differ). */
    background: var(--widget-accent-deep, oklch(50% 0.19 65));
    color: var(--widget-bg-elevated, oklch(99.5% 0.003 250));
  }
  .chip-assignee.mine .chip-initials {
    background: inherit;
    color: inherit;
  }
  @media (prefers-reduced-motion: reduce) {
    .row {
      transition: none;
    }
  }
</style>
